import React from "react";
import { useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { SearchResultItem } from "./SearchResultItem";
import { clsx } from "clsx";
import { TrendingTag } from "./TrendingTag";
import { Tag } from "./Tag";

import "./styles/GlobalSearch.scss";
import { useMeQuery } from "queries";

interface IGlobalSearchProps {
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  currentSearchValue: string;
  setCurrentTag: (value: string[]) => void;
}

const MAX_6_ITEMS = 6;
const MAX_5_ITEMS = 5;
const MAX_4_ITEMS = 4;

export const GlobalSearch = ({
  setSearchInput,
  currentSearchValue,
  setCurrentTag
}: IGlobalSearchProps) => {
  const isSearchBarOpen = useSelector((state: IAppState) => state.application.isSearchBarOpen);
  const { tags: trendingTags, loading: trendingTagsLoading } = useSelector((state: IAppState) => state.search.trendingTags);
  const { creators, loading: creatorsLoading } = useSelector((state: IAppState) => state.search.creators);
  const { niches, loading: nichesLoading } = useSelector((state: IAppState) => state.search.niches);
  const { tags: searchedTags, loading: searchedTagsLoading } = useSelector((state: IAppState) => state.search.tags);
  const { history } = useSelector((state: IAppState) => state.search.history);

  const me = useMeQuery().data;

  const renderTrendingSearches = () => {
    if (!isSearchBarOpen || currentSearchValue?.length) {
      return null;
    }

    if (trendingTagsLoading) {
      return <p className="GlobalSearch-ResultsState GlobalSearch-ResultsState_isSearch">searching...</p>;
    }

    return (
      <div className="GlobalSearch-TrendingTags">
        <h4>🔥️ Trending Searches</h4>
        {!trendingTags.length ? (
          <p className="GlobalSearch-ResultsState">no results for trending tags</p>
        ) : trendingTags.slice(0, MAX_5_ITEMS).map((
          { name }: { name: string }, indx: number
        ) => (
          <TrendingTag
            setCurrentTag={setCurrentTag}
            setSearchInput={setSearchInput}
            title={name}
            key={indx}
          />
        )) }
      </div>
    );
  };

  const renderNiches = () => {
    if (nichesLoading) {
      return <p className="GlobalSearch-ResultsState">searching...</p>;
    }

    if (!niches.length) {
      return <p className="GlobalSearch-ResultsState">no results for niches</p>;
    }

    return (
      <ul className="GlobalSearch-List">
        {niches.slice(0, MAX_4_ITEMS).map((niche: { id: string; text: string }) => (
          <SearchResultItem
            itemType="isNicheItem"
            itemData={niche}
            setSearchInput={setSearchInput}
            key={niche.id}
            setCurrentTag={setCurrentTag}
          />
        ))}
      </ul>
    );
  };

  const renderHistory = () => {
    if (!isSearchBarOpen || !history.length) {
      return null;
    }

    if (!me) {
      return null;
    }

    return (
      <ul className="GlobalSearch-List GlobalSearch-List_History">
        {history.slice(0, MAX_6_ITEMS).map((historyItem: { text: string }, indx: number) => (
          <SearchResultItem
            itemType="isSavedItem"
            setSearchInput={setSearchInput}
            itemData={historyItem}
            key={indx}
            setCurrentTag={setCurrentTag}
          />
        ))}
      </ul>
    );
  };

  const renderCreators = () => {
    if (creatorsLoading) {
      return <p className="GlobalSearch-ResultsState">searching...</p>;
    }

    if (!creators.length) {
      return <p className="GlobalSearch-ResultsState">no results for creators</p>;
    }

    return (
      <ul className="GlobalSearch-List">
        {creators.slice(0, MAX_6_ITEMS).map((creator: { text: string; image: string }, indx: number) => (
          <SearchResultItem
            itemType="isPersonItem"
            setSearchInput={setSearchInput}
            itemData={creator}
            key={indx}
            setCurrentTag={setCurrentTag}
          />
        ))}
      </ul>
    );
  };

  const renderTags = () => {
    if (searchedTagsLoading) {
      return <p className="GlobalSearch-ResultsState">searching...</p>;
    }

    if (!searchedTags.length) {
      return <p className="GlobalSearch-ResultsState">no results for tags</p>;
    }

    return (
      <ul className="GlobalSearch-Tags">
        {searchedTags.map((item: { text: string }, index: number) =>
          <Tag
            title={item.text}
            key={index}
            setSearchInput={setSearchInput}
            setCurrentTag={setCurrentTag}
          />
        )}
      </ul>
    );
  };

  const renderSearchResults = () => {
    if (!isSearchBarOpen || !currentSearchValue?.length) {
      return null;
    }

    return (
      <>
        <div className="GlobalSearch-Row">
          <div className="GlobalSearch-RowItem GlobalSearch-RowItem_isHistory">
            {renderHistory()}
          </div>
          <div className="GlobalSearch-RowItem GlobalSearch-RowItem_isTags">
            {renderTags()}
          </div>
        </div>
        <div className="GlobalSearch-Row">
          <div className="GlobalSearch-RowItem">
            {renderCreators()}
          </div>
          <div className="GlobalSearch-RowItem">
            {renderNiches()}
          </div>
        </div>
      </>
    );
  };

  const renderFirstSearches = () => {
    if (!isSearchBarOpen || currentSearchValue?.length) {
      return null;
    }

    return (
      <>
        {renderHistory()}
        {renderTrendingSearches()}
      </>
    );
  };

  return (
    <div className={clsx(["GlobalSearch", { "GlobalSearch_isOpen": isSearchBarOpen }])}>
      {renderFirstSearches()}
      {renderSearchResults()}
    </div>
  );
};
